import React, { useState } from 'react';
import { authFetch } from '../services/api';

const LandingPageGenerator = () => {
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Form states
  const [generateForm, setGenerateForm] = useState({
    product_name: '',
    target_audience: '',
    conversion_goal: 'lead_generation',
    key_benefits: [],
    industry: 'general',
    template_style: 'modern'
  });

  const [optimizeForm, setOptimizeForm] = useState({
    current_html: '',
    conversion_data: {
      conversion_rate: 2.5,
      bounce_rate: 55,
      time_on_page: 75
    },
    target_improvements: []
  });

  const [variantsForm, setVariantsForm] = useState({
    base_config: {
      product_name: '',
      target_audience: '',
      conversion_goal: 'lead_generation',
      key_benefits: []
    },
    variant_count: 3
  });

  const [benefitInput, setBenefitInput] = useState('');
  const [improvementInput, setImprovementInput] = useState('');

  const conversionGoals = [
    { value: 'lead_generation', label: 'Lead Generation' },
    { value: 'product_sale', label: 'Product Sale' },
    { value: 'newsletter', label: 'Newsletter Signup' },
    { value: 'webinar', label: 'Webinar Registration' },
    { value: 'contact', label: 'Contact Form' }
  ];

  const industries = [
    { value: 'general', label: 'General' },
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'food-beverage', label: 'Food & Beverage' },
    { value: 'marketing', label: 'Marketing' }
  ];

  const templateStyles = [
    { value: 'modern', label: 'Modern' },
    { value: 'classic', label: 'Classic' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'bold', label: 'Bold' },
    { value: 'elegant', label: 'Elegant' }
  ];

  const handleAddBenefit = (benefits, setForm, field) => {
    if (benefitInput.trim()) {
      if (field === 'key_benefits') {
        setForm(prev => ({
          ...prev,
          [field]: [...benefits, benefitInput.trim()]
        }));
      } else {
        // For base_config
        setForm(prev => ({
          ...prev,
          base_config: {
            ...prev.base_config,
            [field]: [...benefits, benefitInput.trim()]
          }
        }));
      }
      setBenefitInput('');
    }
  };

  const handleRemoveBenefit = (index, benefits, setForm, field) => {
    if (field === 'key_benefits') {
      setForm(prev => ({
        ...prev,
        [field]: benefits.filter((_, i) => i !== index)
      }));
    } else {
      // For base_config
      setForm(prev => ({
        ...prev,
        base_config: {
          ...prev.base_config,
          [field]: benefits.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const handleAddImprovement = () => {
    if (improvementInput.trim()) {
      setOptimizeForm(prev => ({
        ...prev,
        target_improvements: [...prev.target_improvements, improvementInput.trim()]
      }));
      setImprovementInput('');
    }
  };

  const handleRemoveImprovement = (index) => {
    setOptimizeForm(prev => ({
      ...prev,
      target_improvements: prev.target_improvements.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (endpoint, formData) => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await authFetch(`/api/marketing/landing-pages/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSubmit = (e) => {
    e.preventDefault();
    handleSubmit('generate', generateForm);
  };

  const handleOptimizeSubmit = (e) => {
    e.preventDefault();
    handleSubmit('optimize', optimizeForm);
  };

  const handleVariantsSubmit = (e) => {
    e.preventDefault();
    handleSubmit('variants', variantsForm);
  };

  const BenefitTags = ({ benefits, onRemove, isBaseConfig = false }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
      {benefits.map((benefit, index) => (
        <span
          key={index}
          className="badge"
          style={{ 
            backgroundColor: 'var(--primary-100)',
            color: 'var(--primary-700)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)'
          }}
          onClick={() => onRemove(index)}
        >
          {benefit}
          <span style={{ 
            fontWeight: 'bold', 
            marginLeft: 'var(--space-1)',
            fontSize: '1.1em'
          }}>
            ×
          </span>
        </span>
      ))}
    </div>
  );

  const ImprovementTags = ({ improvements, onRemove }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
      {improvements.map((improvement, index) => (
        <span
          key={index}
          className="badge"
          style={{ 
            backgroundColor: 'var(--success-100)',
            color: 'var(--success-700)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)'
          }}
          onClick={() => onRemove(index)}
        >
          {improvement}
          <span style={{ 
            fontWeight: 'bold', 
            marginLeft: 'var(--space-1)',
            fontSize: '1.1em'
          }}>
            ×
          </span>
        </span>
      ))}
    </div>
  );

  const ResultsDisplay = () => {
    if (!results) return null;

    if (results.landing_page) {
      const page = results.landing_page;
      return (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Generated Landing Page</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-green-600">SEO Score</h4>
              <div className="text-2xl font-bold">{page.seo_score}/100</div>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-blue-600">Conversion Elements</h4>
              <div className="text-2xl font-bold">{page.conversion_elements.length}</div>
            </div>
          </div>

          {page.performance_predictions && (
            <div className="bg-white p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-2">Performance Predictions</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">Conversion Rate</div>
                  <div className="font-semibold">{page.performance_predictions.conversion_rate}%</div>
                </div>
                <div>
                  <div className="text-gray-600">Bounce Rate</div>
                  <div className="font-semibold">{page.performance_predictions.bounce_rate}%</div>
                </div>
                <div>
                  <div className="text-gray-600">Time on Page</div>
                  <div className="font-semibold">{page.performance_predictions.time_on_page}s</div>
                </div>
                <div>
                  <div className="text-gray-600">Mobile Score</div>
                  <div className="font-semibold">{page.performance_predictions.mobile_score}/100</div>
                </div>
              </div>
            </div>
          )}

          {page.html_content && (
            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">HTML Content</h4>
                <button
                  onClick={() => navigator.clipboard.writeText(page.html_content)}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  Copy HTML
                </button>
              </div>
              <div className="bg-gray-100 p-3 rounded border text-sm font-mono max-h-40 overflow-y-auto">
                {page.html_content}
              </div>
            </div>
          )}

          {page.css_styles && (
            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">CSS Styles</h4>
                <button
                  onClick={() => navigator.clipboard.writeText(page.css_styles)}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Copy CSS
                </button>
              </div>
              <div className="bg-gray-100 p-3 rounded border text-sm font-mono max-h-40 overflow-y-auto">
                {page.css_styles}
              </div>
            </div>
          )}

          {page.optimization_suggestions && page.optimization_suggestions.length > 0 && (
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-yellow-600">Optimization Suggestions</h4>
              <ul className="list-disc pl-5">
                {page.optimization_suggestions.map((suggestion, index) => (
                  <li key={index} className="text-gray-700 text-sm">{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (results.variants) {
      return (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Generated Variants ({results.total_variants})</h3>
          
          <div className="grid gap-6">
            {results.variants.map((variant, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-lg">{variant.variant_id}</h4>
                  <div className="flex gap-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                      SEO: {variant.seo_score}/100
                    </span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                      Conversion: {variant.performance_predictions?.conversion_rate}%
                    </span>
                  </div>
                </div>
                
                <div className="mb-3">
                  <h5 className="font-medium mb-1">Page Title</h5>
                  <p className="text-gray-700">{variant.title}</p>
                </div>

                {variant.conversion_elements && variant.conversion_elements.length > 0 && (
                  <div className="mb-3">
                    <h5 className="font-medium mb-1">Conversion Elements</h5>
                    <div className="flex flex-wrap gap-1">
                      {variant.conversion_elements.slice(0, 3).map((element, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {element.type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => navigator.clipboard.writeText(variant.html_content)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Copy HTML
                  </button>
                  <button
                    onClick={() => navigator.clipboard.writeText(variant.css_styles)}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Copy CSS
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (results.optimization) {
      return (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Page Optimization Results</h3>
          
          {results.optimization.optimized_html && (
            <div className="bg-white p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-2">Optimized HTML</h4>
              <div className="bg-gray-100 p-3 rounded border text-sm font-mono max-h-40 overflow-y-auto">
                {results.optimization.optimized_html}
              </div>
            </div>
          )}

          {results.optimization.expected_improvements && (
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Expected Improvements</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {Object.entries(results.optimization.expected_improvements).map(([key, value]) => (
                  <div key={key}>
                    <div className="text-gray-600 capitalize">{key.replace('_', ' ')}</div>
                    <div className="font-semibold text-green-600">
                      {typeof value === 'number' ? `${value > 0 ? '+' : ''}${value}%` : value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Landing Page Generator
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Landing Page Generator
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Create high-converting landing pages with AI assistance
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{
          display: 'flex',
          gap: 'var(--space-1)',
          borderBottom: '2px solid var(--gray-200)',
          marginBottom: 'var(--space-6)',
          overflowX: 'auto'
        }}>
          {[
            { key: 'generate', label: 'Generate Page', icon: '🚀' },
            { key: 'optimize', label: 'Optimize Page', icon: '⚡' },
            { key: 'variants', label: 'A/B Variants', icon: '🧪' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'none',
                border: 'none',
                borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                cursor: 'pointer',
                fontWeight: '600',
                color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                transition: 'all 0.3s',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Generate Landing Page Tab */}
        {activeTab === 'generate' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              marginBottom: 'var(--space-4)'
            }}>
              Generate New Landing Page
            </h2>
            <form onSubmit={handleGenerateSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-4)' }}>
                <div className="form-group">
                  <label className="form-label required">Product Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={generateForm.product_name}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, product_name: e.target.value }))}
                    placeholder="Enter your product name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Target Audience</label>
                  <input
                    type="text"
                    className="form-input"
                    value={generateForm.target_audience}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="e.g., Small business owners, Marketing professionals"
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
                <div className="form-group">
                  <label className="form-label required">Conversion Goal</label>
                  <select
                    className="form-select"
                    value={generateForm.conversion_goal}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, conversion_goal: e.target.value }))}
                  >
                    {conversionGoals.map(goal => (
                      <option key={goal.value} value={goal.value}>{goal.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Industry</label>
                  <select
                    className="form-select"
                    value={generateForm.industry}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, industry: e.target.value }))}
                  >
                    {industries.map(industry => (
                      <option key={industry.value} value={industry.value}>{industry.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Template Style</label>
                  <select
                    className="form-select"
                    value={generateForm.template_style}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, template_style: e.target.value }))}
                  >
                    {templateStyles.map(style => (
                      <option key={style.value} value={style.value}>{style.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Key Benefits</label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    type="text"
                    className="form-input"
                    style={{ flex: 1 }}
                    value={benefitInput}
                    onChange={(e) => setBenefitInput(e.target.value)}
                    placeholder="Enter a key benefit"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddBenefit(generateForm.key_benefits, setGenerateForm, 'key_benefits');
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleAddBenefit(generateForm.key_benefits, setGenerateForm, 'key_benefits')}
                  >
                    Add
                  </button>
                </div>
                <BenefitTags 
                  benefits={generateForm.key_benefits}
                  onRemove={(index) => handleRemoveBenefit(index, generateForm.key_benefits, setGenerateForm, 'key_benefits')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary btn-block"
              >
                {loading ? 'Generating Landing Page...' : 'Generate Landing Page'}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Optimize Page Tab */}
      {activeTab === 'optimize' && (
        <div className="card">
          <h2 style={{
            color: 'var(--gray-900)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-semibold)',
            marginBottom: 'var(--space-4)'
          }}>
            Optimize Existing Page
          </h2>
          <form onSubmit={handleOptimizeSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label required">Current HTML Code</label>
              <textarea
                className="form-input"
                style={{ 
                  height: '10rem',
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-sm)'
                }}
                value={optimizeForm.current_html}
                onChange={(e) => setOptimizeForm(prev => ({ ...prev, current_html: e.target.value }))}
                placeholder="Paste your current landing page HTML code here..."
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
              <div className="form-group">
                <label className="form-label">Current Conversion Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-input"
                  value={optimizeForm.conversion_data.conversion_rate}
                  onChange={(e) => setOptimizeForm(prev => ({
                    ...prev,
                    conversion_data: {
                      ...prev.conversion_data,
                      conversion_rate: parseFloat(e.target.value) || 0
                    }
                  }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Bounce Rate (%)</label>
                <input
                  type="number"
                  step="1"
                  className="form-input"
                  value={optimizeForm.conversion_data.bounce_rate}
                  onChange={(e) => setOptimizeForm(prev => ({
                    ...prev,
                    conversion_data: {
                      ...prev.conversion_data,
                      bounce_rate: parseInt(e.target.value) || 0
                    }
                  }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Time on Page (seconds)</label>
                <input
                  type="number"
                  step="1"
                  className="form-input"
                  value={optimizeForm.conversion_data.time_on_page}
                  onChange={(e) => setOptimizeForm(prev => ({
                    ...prev,
                    conversion_data: {
                      ...prev.conversion_data,
                      time_on_page: parseInt(e.target.value) || 0
                    }
                  }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Target Improvements</label>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ flex: 1 }}
                  value={improvementInput}
                  onChange={(e) => setImprovementInput(e.target.value)}
                  placeholder="e.g., Increase conversion rate, Reduce bounce rate"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddImprovement();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAddImprovement}
                >
                  Add
                </button>
              </div>
              <ImprovementTags 
                improvements={optimizeForm.target_improvements}
                onRemove={handleRemoveImprovement}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-block"
            >
              {loading ? 'Optimizing Page...' : 'Optimize Page'}
            </button>
          </form>
        </div>
      )}

      {/* Generate Variants Tab */}
      {activeTab === 'variants' && (
        <div className="card">
          <h2 style={{
            color: 'var(--gray-900)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-semibold)',
            marginBottom: 'var(--space-4)'
          }}>
            Generate A/B Test Variants
          </h2>
          <form onSubmit={handleVariantsSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-4)' }}>
              <div className="form-group">
                <label className="form-label required">Product Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={variantsForm.base_config.product_name}
                  onChange={(e) => setVariantsForm(prev => ({
                    ...prev,
                    base_config: { ...prev.base_config, product_name: e.target.value }
                  }))}
                  placeholder="Enter your product name"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Target Audience</label>
                <input
                  type="text"
                  className="form-input"
                  value={variantsForm.base_config.target_audience}
                  onChange={(e) => setVariantsForm(prev => ({
                    ...prev,
                    base_config: { ...prev.base_config, target_audience: e.target.value }
                  }))}
                  placeholder="e.g., Small business owners, Marketing professionals"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
              <div className="form-group">
                <label className="form-label required">Conversion Goal</label>
                <select
                  className="form-select"
                  value={variantsForm.base_config.conversion_goal}
                  onChange={(e) => setVariantsForm(prev => ({
                    ...prev,
                    base_config: { ...prev.base_config, conversion_goal: e.target.value }
                  }))}
                >
                  {conversionGoals.map(goal => (
                    <option key={goal.value} value={goal.value}>{goal.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Number of Variants</label>
                <select
                  className="form-select"
                  value={variantsForm.variant_count}
                  onChange={(e) => setVariantsForm(prev => ({ ...prev, variant_count: parseInt(e.target.value) }))}
                >
                  <option value={2}>2 Variants</option>
                  <option value={3}>3 Variants</option>
                  <option value={4}>4 Variants</option>
                  <option value={5}>5 Variants</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">Key Benefits</label>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ flex: 1 }}
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  placeholder="Enter a key benefit"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBenefit(variantsForm.base_config.key_benefits, setVariantsForm, 'key_benefits');
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => handleAddBenefit(variantsForm.base_config.key_benefits, setVariantsForm, 'key_benefits')}
                >
                  Add
                </button>
              </div>
              <BenefitTags 
                benefits={variantsForm.base_config.key_benefits}
                onRemove={(index) => handleRemoveBenefit(index, variantsForm.base_config.key_benefits, setVariantsForm, 'key_benefits')}
                isBaseConfig={true}
              />
            </div>

            <button
              type="submit"
              disabled={loading || variantsForm.base_config.key_benefits.length === 0}
              className="btn btn-primary btn-block"
            >
              {loading ? 'Generating Variants...' : 'Generate A/B Test Variants'}
            </button>
          </form>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{ 
          marginTop: 'var(--space-6)',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--primary-50)',
          border: '1px solid var(--primary-200)',
          borderRadius: 'var(--border-radius-md)'
        }}>
          <p style={{ color: 'var(--primary-700)', margin: 0 }}>
            <strong>Error:</strong> {error}
          </p>
        </div>
      )}

      {/* Results Display */}
      <ResultsDisplay />
      </div>
    </div>
  );
};

export default LandingPageGenerator;